import FoldersData             from './Entities/FoldersData';
import MailboxQuotaItem        from './Entities/MailboxQuotaItem';
import FoldersHTTPMapping      from './HTTP/FoldersHTTPMapping';
import ViewportHTTPMapping     from './HTTP/ViewportHTTPMapping';
import QuotaHTTPMapping        from './HTTP/QuotaHTTPMapping';
import ViewportCollection      from './Collections/ViewportCollection';
import HTTPResponseEntity      from "@/js/Shared/Services/HTTP/Entities/HTTPResponseEntity";
import NotificationsController from "@/js/Webmail/Notifications/NotificationsController";
import EventProgress           from '@/js/Shared/Events/EventProgress';
import {reactive}              from 'vue';

export {MailboxControllerSingleton as default};

/**
* Mailbox Controller
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class MailboxController {
  /**
   * Folders Data
   *
   * @public
   *
   * @type {FoldersData}
   *
   * @since 7.0.0
   */
  folders = reactive(new FoldersData());

  /**
   * Main Folders entity
   *
   * @public
   *
   * @type {ViewportCollection}
   *
   * @since 7.0.0
   */
  viewport = reactive(new ViewportCollection('folder_id'));

  /**
   * MailboxQuotaItem
   *
   * @public
   *
   * @type {MailboxQuotaItem}
   *
   * @since 7.0.0
   */
  quota = new MailboxQuotaItem();

  /**
   * QuotaHTTPMapping
   *
   * @private
   *
   * @type {QuotaHTTPMapping}
   *
   * @since 7.0.0
   */
  quota_mapping = new QuotaHTTPMapping();

  /**
   * Folders Mapping
   *
   * @private
   *
   * @type {FoldersHTTPMapping}
   *
   * @since 7.0.0
   */
  folders_mapping = new FoldersHTTPMapping();

  /**
   * Viewport Mapping
   *
   * @private
   *
   * @type {ViewportHTTPMapping}
   *
   * @since 7.0.0
   */
  viewport_mapping = new ViewportHTTPMapping();

  /**
   * Mailbox event progress
   *
   * @public
   *
   * @type {EventProgress}
   *
   * @since 7.0.0
   */
  event_progress = new EventProgress();

  /**
   * Updates mailboxes
   *
   * Handles all horde tasks
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {HTTPResponseEntity} response response data
   *
   * @return {Promise.<void>}
   */
  handleResponse(response) {
    if (response.messages) {
      this.handleMessages(response);
    }

    if (response.results) {
      this.handleTasks(response.results);
    }
  }

  /**
   * Handles mailbox tasks
   *
   * Handles all horde tasks
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object} results results data
   *
   * @return {Promise<void>}
   */
  handleTasks(results) {
    if (results['imp:viewport']) {
      this.viewport_mapping.mapViewport(this.viewport, results['imp:viewport']);
    }

    if (results['imp:flag']) {
      this.viewport_mapping.mapFlags(this.viewport, results['imp:flag']);
    }

    if (results['imp:mdn']) {
      this.viewport_mapping.mapMDN(this.viewport, results['imp:mdn']);
    }

    if (results['imp:message']) {
      this.viewport_mapping.mapMessage(this.viewport, results['imp:message']);
    }

    if (results['imp:mailbox']) {
      this.folders_mapping.mapFolders(this.folders, results['imp:mailbox']);
    }

    if (results['imp:poll']) {
      this.folders_mapping.mapFoldersPoll(this.folders, results['imp:poll']);
    }

    if (results['imp:quota']) {
      this.quota_mapping.mapFolderQuota(this.quota, results['imp:quota']);
    }
  }

  /**
   * Handles horder server messages
   *
   * Display notification notices with service messages
   *
   * @note Message handling for horde requests is very inconsistent,
   *       sometimes the server returns a message, sometimes it doesn't,
   *       as of now we ignore everything that is not an error and handle success messages in the client itself
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {HTTPResponseEntity} response response data
   *
   * @return {Promise.<void>}
   */
  handleMessages(response) {
       let type = {
      // 'horde.message' : 'addSuccess',
      // 'horde.success' : 'addSuccess',
      'horde.error'   : 'addError',
    };

    response.messages.forEach((message) => {
      if (!type[message.type]) {
        return;
      }

      NotificationsController[type[message.type]](message.message);
      response.success = false;
    })
  }
}

/**
 * @type {MailboxController}
 */
const MailboxControllerSingleton = new MailboxController();